import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Sun, CloudRain, Wind, Snowflake, Cloud, Trash2, Users, Truck, Package, FileText, ChevronRight, Camera, Download, Mail, Sparkles, Wand2, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";
import { todayIso } from "@/lib/dates";

const WEATHER_OPTS = [
  { v: "fine", label: "Fine", icon: Sun },
  { v: "cloudy", label: "Cloudy", icon: Cloud },
  { v: "rain", label: "Rain", icon: CloudRain },
  { v: "wind", label: "Wind", icon: Wind },
  { v: "snow", label: "Snow", icon: Snowflake },
];

export function DailyLogsTab({ jobId, project }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null); // 'new' | log object | null
  const [emailFor, setEmailFor] = useState(null); // log object | null
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/pm/daily-logs", { params: { job_id: jobId } });
      setItems(data.items || []);
    } catch (e) { setError(e.response?.data?.detail || "Could not load the site diary."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const del = async (id) => {
    if (!confirm("Delete this daily log?")) return;
    await api.delete(`/pm/daily-logs/${id}`);
    load();
  };

  const downloadPdf = async (log) => {
    try {
      const { data } = await api.get(`/pm/daily-logs/${log.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-diary-${log.date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-zinc-600">
          {loading ? "Loading…" : `${items.length} entr${items.length === 1 ? "y" : "ies"} for `}<b>{project?.title}</b>
        </div>
        <Button onClick={() => setOpen("new")} className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white min-h-11 text-sm font-semibold" data-testid="pm-daily-new">
          <Plus className="w-3.5 h-3.5 mr-2" /> New diary entry
        </Button>
      </div>

      <details className="border-b border-border pb-3"><summary className="cursor-pointer py-2 text-sm font-medium">Automatic diary &amp; email settings</summary><div className="mt-2"><AutoSendSettings jobId={jobId} /></div></details>
      {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error} <Button variant="outline" size="sm" onClick={load}>Retry</Button></p>}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <FileText className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No diary entries yet. Start an entry and use Auto-fill from site data to bring in the day’s work.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((log) => {
          const isOpen = expanded[log.id];
          return (
            <div key={log.id} className="hard-card p-4" data-testid={`pm-daily-row-${log.id}`}>
              <button onClick={() => setExpanded({ ...expanded, [log.id]: !isOpen })} className="w-full flex items-start gap-3 text-left">
                <ChevronRight className={`w-4 h-4 mt-1 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold uppercase tracking-tight text-base">{new Date(log.date + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}</span>
                    {log.weather && <WeatherChip w={log.weather} t={log.temp_c} />}
                    {log.site_open === false && <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-900 border border-red-300 px-1.5 py-0.5">Site closed</span>}
                  </div>
                  {log.narrative && !isOpen && <div className="text-xs text-zinc-500 truncate mt-1">{log.narrative}</div>}
                </div>
                <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 shrink-0">
                  {log.manpower?.length > 0 && <span title="Trades on site"><Users className="w-3 h-3 inline" /> {log.manpower.reduce((a, m) => a + (m.count || 0), 0)}</span>}
                  {log.deliveries?.length > 0 && <span title="Deliveries"><Truck className="w-3 h-3 inline" /> {log.deliveries.length}</span>}
                  {log.plant_on_site?.length > 0 && <span title="Plant"><Package className="w-3 h-3 inline" /> {log.plant_on_site.length}</span>}
                  {log.photos?.length > 0 && <span title="Photos"><Camera className="w-3 h-3 inline" /> {log.photos.length}</span>}
                </div>
              </button>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3 text-sm">
                  {log.narrative && (<div><div className="label-tag">Narrative</div><p className="mt-1.5 text-zinc-700 whitespace-pre-wrap">{log.narrative}</p></div>)}
                  {log.delays && (<div><div className="label-tag !text-red-700">Delays</div><p className="mt-1.5 text-zinc-700">{log.delays}</p></div>)}
                  {log.visitors && (<div><div className="label-tag">Visitors</div><p className="mt-1.5 text-zinc-700">{log.visitors}</p></div>)}
                  {log.manpower?.length > 0 && (
                    <div><div className="label-tag">Manpower</div>
                      <ul className="mt-1.5 text-xs space-y-0.5">{log.manpower.map((m, i) => (<li key={i}>• {m.count} × {m.trade}{m.hours ? ` (${m.hours}h)` : ""}</li>))}</ul></div>
                  )}
                  {log.deliveries?.length > 0 && (
                    <div><div className="label-tag">Deliveries</div>
                      <ul className="mt-1.5 text-xs space-y-0.5">{log.deliveries.map((d, i) => (<li key={i}>• {d.supplier} — {d.item}{d.quantity ? ` (${d.quantity})` : ""}</li>))}</ul></div>
                  )}
                  {log.plant_on_site?.length > 0 && (
                    <div><div className="label-tag">Plant on site</div>
                      <ul className="mt-1.5 text-xs space-y-0.5">{log.plant_on_site.map((p, i) => (<li key={i}>• {p.item}{p.hours ? ` — ${p.hours}h` : ""}</li>))}</ul></div>
                  )}
                  {log.photos?.length > 0 && (
                    <div><div className="label-tag">Photos ({log.photos.length})</div>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                        {log.photos.map((p) => (<AuthImage key={p} path={p} className="w-full h-24 object-cover border border-zinc-200" />))}
                      </div></div>
                  )}
                  <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
                    <span className="text-[10px] font-mono text-zinc-400 mr-auto">Logged by {log.created_by_name}{log.emails_sent?.length ? ` · emailed ×${log.emails_sent.length}` : ""}</span>
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(log)} className="rounded-sm text-xs h-7" data-testid={`pm-daily-pdf-${log.id}`}><Download className="w-3 h-3 mr-1" /> PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => setEmailFor(log)} className="rounded-sm text-xs h-7" data-testid={`pm-daily-email-${log.id}`}><Mail className="w-3 h-3 mr-1" /> Email</Button>
                    <Button size="sm" variant="outline" onClick={() => setOpen(log)} className="rounded-sm text-xs h-7" data-testid={`pm-daily-edit-${log.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="rounded-sm text-xs h-7 text-red-600" onClick={() => del(log.id)} data-testid={`pm-daily-delete-${log.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {open && <DailyLogEditor jobId={jobId} initial={open === "new" ? null : open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      {emailFor && <EmailDiaryDialog log={emailFor} onClose={() => setEmailFor(null)} onSent={() => { setEmailFor(null); load(); }} />}
    </div>
  );
}

function WeatherChip({ w, t }) {
  const opt = WEATHER_OPTS.find((o) => o.v === w);
  if (!opt) return null;
  const Icon = opt.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-blue-50 border border-blue-200 text-blue-900 px-1.5 py-0.5 dark:bg-blue-500/15 dark:border-blue-700/50 dark:text-blue-300">
      <Icon className="w-3 h-3" /> {opt.label}{Number.isFinite(t) ? ` · ${t}°C` : ""}
    </span>
  );
}

function DailyLogEditor({ jobId, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    date: initial?.date || todayIso(),
    weather: initial?.weather || "fine",
    temp_c: initial?.temp_c ?? "",
    rainfall_mm: initial?.rainfall_mm ?? "",
    site_open: initial?.site_open ?? true,
    site_closed_reason: initial?.site_closed_reason || "",
    narrative: initial?.narrative || "",
    delays: initial?.delays || "",
    visitors: initial?.visitors || "",
    manpower: initial?.manpower || [],
    deliveries: initial?.deliveries || [],
    plant_on_site: initial?.plant_on_site || [],
    photos: initial?.photos || [],
  }));
  const [busy, setBusy] = useState(false);
  const [filling, setFilling] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [savedId, setSavedId] = useState(initial?.id || null);

  const autoFill = async () => {
    setFilling(true);
    try {
      const { data } = await api.get("/pm/daily-logs/prefill", { params: { job_id: jobId, date: form.date } });
      const wx = data.weather_auto;
      const hasActivity = data.sources?.timesheets || data.sources?.photos || data.sources?.tasks;
      if (!hasActivity && !wx) {
        toast.message("Nothing logged for that date yet — no timesheets, tasks or photos found.");
        return;
      }
      setForm((f) => ({
        ...f,
        ...(wx ? {
          weather: wx.weather || f.weather,
          temp_c: wx.temp_c ?? f.temp_c,
          rainfall_mm: wx.rainfall_mm ?? f.rainfall_mm,
        } : {}),
        manpower: data.manpower?.length ? data.manpower : f.manpower,
        plant_on_site: data.plant_on_site?.length ? data.plant_on_site : f.plant_on_site,
        photos: [...new Set([...(f.photos || []), ...(data.photos || [])])],
        narrative: f.narrative?.trim() ? f.narrative : (data.narrative || f.narrative),
      }));
      const bits = [];
      if (hasActivity) bits.push(`${data.sources.timesheets} timesheet${data.sources.timesheets === 1 ? "" : "s"}, ${data.sources.photos} photo${data.sources.photos === 1 ? "" : "s"}`);
      if (wx) bits.push(`weather: ${wx.weather}${wx.temp_c != null ? ` ${wx.temp_c}°C` : ""}`);
      toast.success(`Pulled ${bits.join(" · ")} from site data`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fill failed");
    } finally { setFilling(false); }
  };

  const writeWithAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/pm/daily-logs/ai-narrative", { job_id: jobId, date: form.date });
      setForm((f) => ({ ...f, narrative: data.narrative }));
      toast.success("Narrative written — give it a once-over");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI narrative failed");
    } finally { setAiBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        temp_c: form.temp_c === "" ? null : Number(form.temp_c),
        rainfall_mm: form.rainfall_mm === "" ? null : Number(form.rainfall_mm),
      };
      if (savedId) {
        await api.patch(`/pm/daily-logs/${savedId}`, payload);
        toast.success("Daily log saved");
      } else {
        const { data } = await api.post("/pm/daily-logs", { job_id: jobId, ...payload });
        setSavedId(data.id);
        toast.success("Daily log created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!savedId) { toast.error("Save the log first, then add photos."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pm/daily-logs/${savedId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...(f.photos || []), data.storage_path] }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  // Helpers for list manipulation
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...f[key], row] }));
  const updateRow = (key, idx, patch) => setForm((f) => ({ ...f, [key]: f[key].map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const removeRow = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pm-daily-editor">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{savedId ? "Edit diary entry" : "New diary entry"}</DialogTitle>
          <DialogDescription>One entry per job per day. Review the site record before sharing it.</DialogDescription>
        </DialogHeader>

        <button type="button" onClick={autoFill} disabled={filling || busy}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#FF5F15]/60 text-[#FF5F15] hover:bg-[#FF5F15]/5 font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm transition-colors disabled:opacity-50"
                data-testid="pm-daily-autofill">
          <Wand2 className="w-4 h-4" />
          {filling ? "Gathering site data…" : "Auto-fill from site data — crew, plant, photos, tasks"}
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="label-tag">Date *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-daily-date" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tag">Weather</Label>
            <div className="mt-2 flex flex-wrap gap-1">
              {WEATHER_OPTS.map((w) => {
                const Icon = w.icon;
                const on = form.weather === w.v;
                return (
                  <button key={w.v} type="button" onClick={() => setForm({ ...form, weather: w.v })} className={`px-2.5 py-2 text-xs font-bold uppercase tracking-wider border-2 rounded-sm flex items-center gap-1.5 transition-colors ${on ? "border-[#FF5F15] bg-[#FF5F15] text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-400"}`} data-testid={`pm-daily-weather-${w.v}`}>
                    <Icon className="w-3 h-3" /> {w.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="label-tag">Temp (°C)</Label>
            <Input type="number" step="0.5" value={form.temp_c} onChange={(e) => setForm({ ...form, temp_c: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-daily-temp" />
          </div>
          <div>
            <Label className="label-tag">Rainfall (mm)</Label>
            <Input type="number" step="0.1" value={form.rainfall_mm} onChange={(e) => setForm({ ...form, rainfall_mm: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-daily-rain" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
              <input type="checkbox" checked={!form.site_open} onChange={(e) => setForm({ ...form, site_open: !e.target.checked })} data-testid="pm-daily-closed" />
              <span className="font-bold">Site closed today</span>
            </label>
          </div>
        </div>

        {!form.site_open && (
          <div>
            <Label className="label-tag">Reason</Label>
            <Input value={form.site_closed_reason} onChange={(e) => setForm({ ...form, site_closed_reason: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Rained off, holiday, no materials" data-testid="pm-daily-closed-reason" />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            <Label className="label-tag">Narrative — what happened today</Label>
            <button type="button" onClick={writeWithAi} disabled={aiBusy || busy}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100] disabled:opacity-50"
                    data-testid="pm-daily-ai-narrative">
              <Sparkles className="w-3 h-3" /> {aiBusy ? "Writing…" : "Write with AI"}
            </button>
          </div>
          <Textarea rows={4} value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="Excavated 12m of trench in section A. TM setup at 7am, packed down 4pm…" data-testid="pm-daily-narrative" />
        </div>

        <ListBuilder
          label="Manpower" addLabel="Add trade" testId="pm-daily-manpower"
          items={form.manpower}
          onAdd={() => addRow("manpower", { trade: "", count: 1, hours: 8 })}
          onRemove={(i) => removeRow("manpower", i)}
          renderRow={(row, i) => (
            <div className="grid grid-cols-12 gap-2">
              <Input value={row.trade} onChange={(e) => updateRow("manpower", i, { trade: e.target.value })} placeholder="Trade (e.g. foreman, labourer)" className="col-span-7 rounded-sm border-zinc-400 h-9 text-sm" />
              <Input type="number" min="0" value={row.count} onChange={(e) => updateRow("manpower", i, { count: Number(e.target.value) || 0 })} placeholder="#" className="col-span-2 rounded-sm border-zinc-400 h-9 text-sm" />
              <Input type="number" step="0.25" min="0" value={row.hours ?? ""} onChange={(e) => updateRow("manpower", i, { hours: e.target.value === "" ? null : Number(e.target.value) })} placeholder="h" className="col-span-3 rounded-sm border-zinc-400 h-9 text-sm" />
            </div>
          )}
        />

        <ListBuilder
          label="Deliveries" addLabel="Add delivery" testId="pm-daily-deliveries"
          items={form.deliveries}
          onAdd={() => addRow("deliveries", { supplier: "", item: "", quantity: "" })}
          onRemove={(i) => removeRow("deliveries", i)}
          renderRow={(row, i) => (
            <div className="grid grid-cols-12 gap-2">
              <Input value={row.supplier} onChange={(e) => updateRow("deliveries", i, { supplier: e.target.value })} placeholder="Supplier" className="col-span-4 rounded-sm border-zinc-400 h-9 text-sm" />
              <Input value={row.item} onChange={(e) => updateRow("deliveries", i, { item: e.target.value })} placeholder="Item" className="col-span-5 rounded-sm border-zinc-400 h-9 text-sm" />
              <Input value={row.quantity || ""} onChange={(e) => updateRow("deliveries", i, { quantity: e.target.value })} placeholder="Qty" className="col-span-3 rounded-sm border-zinc-400 h-9 text-sm" />
            </div>
          )}
        />

        <ListBuilder
          label="Plant on site" addLabel="Add plant" testId="pm-daily-plant"
          items={form.plant_on_site}
          onAdd={() => addRow("plant_on_site", { item: "", hours: 8 })}
          onRemove={(i) => removeRow("plant_on_site", i)}
          renderRow={(row, i) => (
            <div className="grid grid-cols-12 gap-2">
              <Input value={row.item} onChange={(e) => updateRow("plant_on_site", i, { item: e.target.value })} placeholder="e.g. 5T excavator, vibe roller" className="col-span-9 rounded-sm border-zinc-400 h-9 text-sm" />
              <Input type="number" step="0.25" min="0" value={row.hours ?? ""} onChange={(e) => updateRow("plant_on_site", i, { hours: e.target.value === "" ? null : Number(e.target.value) })} placeholder="h" className="col-span-3 rounded-sm border-zinc-400 h-9 text-sm" />
            </div>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Delays / issues</Label>
            <Textarea rows={2} value={form.delays} onChange={(e) => setForm({ ...form, delays: e.target.value })} className="mt-2 rounded-sm border-zinc-400 text-sm" placeholder="Rain stopped work 2-3pm. Awaiting delivery from XYZ." />
          </div>
          <div>
            <Label className="label-tag">Visitors</Label>
            <Textarea rows={2} value={form.visitors} onChange={(e) => setForm({ ...form, visitors: e.target.value })} className="mt-2 rounded-sm border-zinc-400 text-sm" placeholder="WorkSafe inspector. Engineer site walk." />
          </div>
        </div>

        {/* Photos */}
        <div>
          <Label className="label-tag">Photos {savedId ? `(${form.photos.length})` : "(save first to add)"}</Label>
          {form.photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
              {form.photos.map((p) => <AuthImage key={p} path={p} className="w-full h-20 object-cover border border-zinc-200" />)}
            </div>
          )}
          <input
            type="file" accept="image/*" capture="environment"
            onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
            disabled={!savedId || busy}
            className="mt-2 text-xs"
            data-testid="pm-daily-photo-upload" />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Close</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-daily-save">
            {savedId ? "Save changes" : "Create log"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Per-job diary auto-send: recipients + evening toggle.
function AutoSendSettings({ jobId }) {
  const [recipients, setRecipients] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    setLoaded(false);
    api.get(`/pm/jobs/${jobId}/diary-settings`).then(({ data }) => {
      setRecipients((data.recipients || []).join(", "));
      setAutoSend(!!data.auto_send);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [jobId]);

  const save = async (nextAutoSend = autoSend) => {
    setSaving(true);
    try {
      const list = recipients.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
      const { data } = await api.patch(`/pm/jobs/${jobId}/diary-settings`, { recipients: list, auto_send: nextAutoSend });
      setRecipients((data.recipients || []).join(", "));
      if (nextAutoSend && !data.recipients?.length) {
        toast.message("Add at least one recipient email or nothing will be sent");
      } else {
        toast.success(nextAutoSend ? "Auto-send on — diary emails itself each evening" : "Diary settings saved");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;
  return (
    <div className="hard-card p-4 flex flex-wrap items-end gap-4" data-testid="diary-autosend-card">
      <div className="flex-1 min-w-[240px]">
        <Label className="label-tag flex items-center gap-1.5"><Send className="w-3 h-3" /> Diary recipients — client / engineer emails</Label>
        <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} onBlur={() => save()}
               placeholder="engineer@client.co.nz, pm@council.govt.nz"
               className="mt-2 rounded-sm border-zinc-400 text-sm" data-testid="diary-recipients-input" />
      </div>
      <div className="flex items-center gap-2 pb-1.5">
        <Switch checked={autoSend} disabled={saving}
                onCheckedChange={(v) => { setAutoSend(v); save(v); }}
                data-testid="diary-autosend-toggle" />
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
          Auto-send each evening
        </span>
      </div>
    </div>
  );
}

function EmailDiaryDialog({ log, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const emails = to.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!emails.length) { toast.error("Enter at least one email address"); return; }
    setSending(true);
    try {
      await api.post(`/pm/daily-logs/${log.id}/email`, { to: emails, message: message.trim() || null });
      toast.success(`Diary emailed to ${emails.join(", ")}`);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="pm-daily-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">Email site diary</DialogTitle>
          <DialogDescription>
            Sends the {new Date(log.date + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short" })} diary as a branded PDF attachment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">To — separate multiple with commas</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="engineer@client.co.nz, pm@council.govt.nz" className="mt-2 rounded-sm border-zinc-400" data-testid="pm-daily-email-to" />
          </div>
          <div>
            <Label className="label-tag">Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Kia ora — attached is yesterday's site diary for your records." className="mt-2 rounded-sm border-zinc-400 text-sm" data-testid="pm-daily-email-message" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-daily-email-send">
            <Mail className="w-3.5 h-3.5 mr-2" /> {sending ? "Sending…" : "Send PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListBuilder({ label, addLabel, items, onAdd, onRemove, renderRow, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="label-tag">{label}</Label>
        <button type="button" onClick={onAdd} className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100]" data-testid={`${testId}-add`}>
          + {addLabel}
        </button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-[11px] font-mono text-zinc-400 italic">None yet.</div>}
        {items.map((row, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${testId}-row-${i}`}>
            <div className="flex-1 min-w-0">{renderRow(row, i)}</div>
            <button type="button" onClick={() => onRemove(i)} className="text-zinc-400 hover:text-red-600 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
